import { createApp } from 'vue';
import { createPinia } from 'pinia';

import * as Sentry from '@sentry/vue';

import App from './App.vue';
import router from './router/router';

import '@/assets/tailwind.css';
import '@prospective/lithium/lithium.css';
import '@/assets/lead.scss';

import VToast, { POSITION as VTPosition, TYPE as VTType } from 'vue-toastification';
import type { PluginOptions } from 'vue-toastification';

import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

import { Dropdown, vTooltip } from 'floating-vue';
import 'floating-vue/dist/style.css';
import FloatingVue from 'floating-vue';

import { authorizeClient } from './api/client';
import { brand } from '@/config/brands';
import { applyBrandTheme } from '@/config/brands/applyBrandTheme';

import translator from './locale/translator.js';
import { TranslatorPlugin } from '@prospective/lithium';

import { Dispatcher } from '@/events/dispatcher';

import PreviewModalPlugin from '@/plugins/previewModal';
import AutopilotRouterPlugin from '@/autopilot/routerPlugin';

/**
 * Setting the locales
 */
import { enUS } from 'date-fns/locale';
import { de } from 'date-fns/locale';

applyBrandTheme(brand);

const app = createApp(App);
const pinia = createPinia();
const dispatcher = new Dispatcher();
const hostname = import.meta.env.VITE_PJAS_HOSTNAME ?? `${window.location.protocol}//${window.location.hostname}`;

/**
 * Sentry configuration
 *
 * We set the tracePropagationTargets to empty array in development because we don't want to send requests to Sentry.
 * If we are in stage / production, we want to send requests to Sentry, so we add the hostname appended with /v1 to the tracePropagationTargets.
 * The /v1 is added because we want to send requests to Sentry only for the API calls, which are all prefixed with /v1.
 * This way we avoid sending requests to Sentry for other calls, such as static assets or other third-party services.
 */
const tracePropagationTargets = new Array<string>();
if (import.meta.env.PROD) {
  tracePropagationTargets.push(`${hostname}/v1`);
}

const toastOptions: PluginOptions = {
  timeout: 7000,
  transition: 'Vue-Toastification__bounce',
  position: VTPosition.TOP_CENTER,
  toastDefaults: {
    [VTType.ERROR]: {
      icon: 'ti ti-alert-octagon',
    },
    [VTType.WARNING]: {
      icon: 'ti ti-alert-triangle',
    },
    [VTType.SUCCESS]: {
      icon: 'ti ti-check',
    },
  },
};

Sentry.init({
  app,
  dsn: 'https://822f2198c2eb508c77428d915f8b58d2@o927288.ingest.sentry.io/4506592492781568',
  integrations: [
    Sentry.browserTracingIntegration({
      router,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracePropagationTargets,
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

authorizeClient().then(async () => {
  /**
   * Defaults
   */
  window.__pjas_date_locale = {
    en_US: enUS,
    de_DE: de,
    de_CH: de,
  };
  window.__pjas_locale = 'en_US';

  app.use(VToast, toastOptions);
  app.component('VSelect', vSelect);

  app.component('VDropdown', Dropdown);
  app.directive('tooltip', vTooltip);
  FloatingVue.options.themes.tooltip.popperHideTriggers = ['hover'];

  app.provide('dispatcher', dispatcher);
  app.use(PreviewModalPlugin, dispatcher);

  app.use(pinia);
  app.use(router);
  app.use(AutopilotRouterPlugin, { router });
  app.use(TranslatorPlugin, { translator });

  app.mount('#app');
});
